// =============================================================================
// permission_route.js
// Route guard that checks both authentication AND a specific module permission.
//
// Usage:
//   <PermissionRoute
//     path="/messages"
//     module="messaging.messages"
//     level="view"          // optional, defaults to 'view'
//     component={Messages}
//   />
//
// Behavior:
//   - Not authenticated      → redirect to /auth/login
//   - Authenticated, no perm → redirect to /unauthorized  (or renders fallback)
//   - Permissions not yet loaded → render null (prevents flash)
//   - Owner → always passes through
// =============================================================================

import React, { useContext } from "react";
import { Route, Redirect } from "react-router-dom";
import moment from "moment";
import { AppContexts } from "./providers";
import { usePermissionsStore } from "./components/permissions/permissions_store";

const PermissionRoute = ({
  component: Component,
  module,
  level = "view",
  ...rest
}) => {
  const authCtx = useContext(AppContexts.AuthContext);
  const { canView, canEdit, permsLoaded } = usePermissionsStore();

  return (
    <Route
      {...rest}
      render={(props) => {
        // --- Auth checks (same as AuthorizedRoute) ---
        if (!authCtx.user) {
          return (
            <Redirect
              to={{ pathname: "/auth/login", state: { from: props.location } }}
            />
          );
        }

        if (
          authCtx.user.details?.type === "expired" ||
          moment(
            ["trial", "trial-full"].includes(authCtx.user.details?.type) &&
              authCtx.user.details?.trial_end,
          ) < moment()
        ) {
          return (
            <Redirect
              to={{ pathname: "/expired", state: { from: props.location } }}
            />
          );
        }

        if (
          authCtx.user.details?.type === "onboarding" &&
          rest.path !== "/onboarding"
        ) {
          return (
            <Redirect
              to={{ pathname: "/onboarding", state: { from: props.location } }}
            />
          );
        }

        // --- Permission check ---
        // Wait for permissions to load before rendering (avoid flash of unauthorized)
        if (!permsLoaded) return null;

        // Check the required module permission
        if (module) {
          const allowed =
            level === "edit" ? canEdit(module) : canView(module);
          if (!allowed) {
            return (
              <Redirect
                to={{
                  pathname: "/unauthorized",
                  state: { from: props.location, module },
                }}
              />
            );
          }
        }

        return <Component {...props} />;
      }}
    />
  );
};

export default PermissionRoute;
